import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Download, CheckCircle, Settings, Mail, Phone, Filter } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import FiltrationCTABanner from '../components/FiltrationCTABanner';
import CleaningOptionsComparison from '../components/CleaningOptionsComparison';
import AlternativesBand from '../components/AlternativesBand';
import UnifiedContactModal from '../components/UnifiedContactModal';

const BIGSSeriesPage = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="BIG-S Stainless In-Line Filters | Precision Gasket-Sealed Filtration"
        description="Stainless-only, cost-optimized in-line filters with gasket-sealed elements, modular porting, and manual/semi-auto/automatic cleaning options."
        canonicalPath="/filtration/inline/big-s"
      />
      <Navbar />
      <FiltrationCTABanner />

      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to="/" className="text-gray-600 hover:text-ctc-blue">Home</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration" className="text-gray-600 hover:text-ctc-blue">Filtration</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration/inline" className="text-gray-600 hover:text-ctc-blue">In-Line Pressure Filters</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-gray-900 font-semibold">BIG-S (Stainless)</li>
          </ol>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-16 pb-12 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl font-bold text-gray-900 mb-4">
                BIG-S Stainless In-Line Filters
              </h1>
              <p className="text-2xl text-gray-600 mb-8 font-light leading-relaxed">
                Precision, gasket-sealed filtration in a stainless-exclusive, cost-optimized body.
              </p>

              <div className="bg-blue-50 border-l-4 border-ctc-blue p-6 rounded-r-lg mb-8">
                <p className="text-gray-800 leading-relaxed">
                  BIG-S filters are engineered from the ground up for stainless steel construction, offering a more affordable alternative to traditional stainless bodies while maintaining the precision and reliability of gasket-sealed screen technology.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => setIsContactModalOpen(true)}
                  className="bg-ctc-blue text-white px-8 py-4 rounded-lg font-semibold hover:bg-ctc-blue-dark transition-colors shadow-lg"
                >
                  Get a Quote
                </button>
                <Link
                  to="/contact?topic=filtration"
                  className="border-2 border-ctc-blue text-ctc-blue px-8 py-4 rounded-lg font-semibold hover:bg-ctc-blue hover:text-white transition-colors text-center"
                >
                  Talk to a Filtration Specialist
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="space-y-4">
                <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                  <img src="/BIG-S.jpeg" alt="BIG-S Series Stainless In-Line Filter" className="w-full h-auto" />
                  <div className="p-4">
                    <h3 className="font-bold text-gray-900 mb-2">BIG-S Series Filter</h3>
                    <p className="text-sm text-gray-700">Stainless-only, cost-optimized body with precision gasket-sealed filtration.</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                    <img src="/BIG-S Bank.JPG" alt="BIG-S Series Bank Installation" className="w-full h-auto" />
                    <div className="p-3">
                      <h3 className="font-bold text-gray-900 mb-1 text-sm">BIG-S Filter Bank</h3>
                      <p className="text-xs text-gray-700">Multiple units in parallel</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                    <img src="/BIG-S Retro.JPG" alt="BIG-S Series Retrofit Installation" className="w-full h-auto" />
                    <div className="p-3">
                      <h3 className="font-bold text-gray-900 mb-1 text-sm">BIG-S Retrofit</h3>
                      <p className="text-xs text-gray-700">Modular port flexibility</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Cleaning Options Info Strip */}
      <section className="py-6 bg-gradient-to-r from-gray-50 to-green-50 border-y border-gray-300">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="font-bold text-gray-900 mb-2">Three Cleaning Approaches Available</h3>
              <p className="text-gray-700 text-sm">
                Supports Manual, Semi-Automatic (BIF & MVC), and Fully Automatic (RAB & AVC) cleaning options.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/filtration/inline/cleaning/manual"
                className="text-xs bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
              >
                Manual
              </Link>
              <Link
                to="/filtration/inline/cleaning/semi-auto"
                className="text-xs bg-white border border-gray-600 text-gray-700 px-4 py-2 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
              >
                Semi-Auto
              </Link>
              <Link
                to="/filtration/inline/cleaning/automatic"
                className="text-xs bg-white border border-green-600 text-green-600 px-4 py-2 rounded-lg font-semibold hover:bg-green-50 transition-colors"
              >
                Fully Auto
              </Link>
              <Link
                to="/filtration/inline/cleaning"
                className="text-xs bg-gray-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
              >
                Compare All
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Key Benefits</h2>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="text-ctc-blue" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Stainless-Only Value</h3>
              <p className="text-gray-700 leading-relaxed">
                Engineered from the ground up for stainless; more affordable than traditional SS bodies.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-center"
            >
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Filter className="text-green-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Precision & Sealing</h3>
              <p className="text-gray-700 leading-relaxed">
                Gasket-sealed screen seats to the body—no bypass around the element.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-center"
            >
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Settings className="text-orange-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Modular Ports</h3>
              <p className="text-gray-700 leading-relaxed">
                Precision laser-cut, formed, and semi-robotic welded body enables flexible port locations to minimize install costs.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Standard Sizes & Dimensions Table */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">Standard Sizes & Dimensions</h2>
          <p className="text-center text-gray-600 mb-8">
            Flow ranges selected to mirror standard BIG configurations for comparable body sizes
          </p>

          <div className="mb-8 flex justify-center">
            <img
              src="/BIG Dimension Diagram.png"
              alt="BIG-S Dimensional Diagram showing measurements A, B, C, D, E, and F"
              className="max-w-full h-auto"
            />
          </div>

          <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
            <table className="w-full border-collapse">
              <thead className="bg-gradient-to-r from-gray-600 to-gray-700 text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Model</th>
                  <th className="px-4 py-3 text-left font-semibold">Flow Range (gpm)</th>
                  <th className="px-4 py-3 text-left font-semibold">In/Out</th>
                  <th className="px-4 py-3 text-left font-semibold">A</th>
                  <th className="px-4 py-3 text-left font-semibold">B</th>
                  <th className="px-4 py-3 text-left font-semibold">C</th>
                  <th className="px-4 py-3 text-left font-semibold">D</th>
                  <th className="px-4 py-3 text-left font-semibold">E</th>
                  <th className="px-4 py-3 text-left font-semibold">F</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">BA828</td>
                  <td className="px-4 py-3">200–800</td>
                  <td className="px-4 py-3">6FLG</td>
                  <td className="px-4 py-3">34 15/16</td>
                  <td className="px-4 py-3">46 3/16</td>
                  <td className="px-4 py-3">3</td>
                  <td className="px-4 py-3">10</td>
                  <td className="px-4 py-3">56 1/4</td>
                  <td className="px-4 py-3">8 1/2</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">BA830</td>
                  <td className="px-4 py-3">800–1600</td>
                  <td className="px-4 py-3">6FLG</td>
                  <td className="px-4 py-3">80 13/16</td>
                  <td className="px-4 py-3">92 1/8</td>
                  <td className="px-4 py-3">3</td>
                  <td className="px-4 py-3">10</td>
                  <td className="px-4 py-3">102 1/4</td>
                  <td className="px-4 py-3">8 1/2</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">BA831</td>
                  <td className="px-4 py-3">750–1200</td>
                  <td className="px-4 py-3">10FLG</td>
                  <td className="px-4 py-3">35 3/4</td>
                  <td className="px-4 py-3">46 1/8</td>
                  <td className="px-4 py-3">4</td>
                  <td className="px-4 py-3">14</td>
                  <td className="px-4 py-3">58 7/8</td>
                  <td className="px-4 py-3">12 1/4</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">BA832</td>
                  <td className="px-4 py-3">1000–2200</td>
                  <td className="px-4 py-3">10FLG</td>
                  <td className="px-4 py-3">81 5/8</td>
                  <td className="px-4 py-3">92</td>
                  <td className="px-4 py-3">4</td>
                  <td className="px-4 py-3">14</td>
                  <td className="px-4 py-3">104 3/4</td>
                  <td className="px-4 py-3">12 1/4</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">BA833</td>
                  <td className="px-4 py-3">2000–3200</td>
                  <td className="px-4 py-3">10FLG</td>
                  <td className="px-4 py-3">127 1/2</td>
                  <td className="px-4 py-3">137 7/8</td>
                  <td className="px-4 py-3">4</td>
                  <td className="px-4 py-3">14</td>
                  <td className="px-4 py-3">150 11/16</td>
                  <td className="px-4 py-3">12 1/4</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-sm text-gray-600 mt-4 text-center italic">
            Flow ranges selected to mirror standard BIG configurations for comparable body sizes; confirm with a CTC specialist for your water quality and screen selection.
          </p>
        </div>
      </section>

      {/* Screen Options */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Screen Options</h2>

          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              whileHover={{ y: -5 }}
              className="bg-white rounded-lg shadow-lg p-8 border-2 border-gray-200 hover:border-ctc-blue transition-all"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Perforated Round-Hole</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-ctc-blue mr-2">•</span>
                  <span>High open area for maximum flow</span>
                </li>
                <li className="flex items-start">
                  <span className="text-ctc-blue mr-2">•</span>
                  <span>Ideal for coarse filtration and high solids</span>
                </li>
                <li className="flex items-start">
                  <span className="text-ctc-blue mr-2">•</span>
                  <span>Available in standard increments</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              whileHover={{ y: -5 }}
              className="bg-white rounded-lg shadow-lg p-8 border-2 border-gray-200 hover:border-ctc-blue transition-all"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Woven Mesh</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-ctc-blue mr-2">•</span>
                  <span>Fine filtration for drip and micro-irrigation</span>
                </li>
                <li className="flex items-start">
                  <span className="text-ctc-blue mr-2">•</span>
                  <span>Standard mesh sizes (20–100)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-ctc-blue mr-2">•</span>
                  <span>Precision control of particle size</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Cleaning Options */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <CleaningOptionsComparison series="BIG-S" />
        </div>
      </section>

      {/* Custom Configurations */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Custom Configurations</h2>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="bg-gray-100 rounded-lg p-12 text-center">
                <Filter size={80} className="mx-auto text-gray-400 mb-4" />
                <p className="text-sm text-gray-600">Diagram placeholder</p>
              </div>
            </div>

            <div>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                Port orientation and nozzle arrangements can be customized to reduce elbows, trenching, and install labor. Request a layout review.
              </p>
              <button
                onClick={() => setIsContactModalOpen(true)}
                className="bg-ctc-blue text-white px-8 py-4 rounded-lg font-semibold hover:bg-ctc-blue-dark transition-colors shadow-lg"
              >
                Request Layout Review
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Downloads */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Downloads</h2>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="space-y-4">
              <a
                href="#"
                onClick={(e) => e.preventDefault()}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center">
                  <Download className="text-ctc-blue mr-4" size={24} />
                  <span className="font-semibold text-gray-900">BIG-S Data Sheet (PDF)</span>
                </div>
                <ArrowRight className="text-gray-400" size={20} />
              </a>

              <a
                href="#"
                onClick={(e) => e.preventDefault()}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center">
                  <Download className="text-ctc-blue mr-4" size={24} />
                  <span className="font-semibold text-gray-900">Installation & Maintenance Manual (PDF)</span>
                </div>
                <ArrowRight className="text-gray-400" size={20} />
              </a>

              <a
                href="#"
                onClick={(e) => e.preventDefault()}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center">
                  <Download className="text-ctc-blue mr-4" size={24} />
                  <span className="font-semibold text-gray-900">Dimensional Drawing Pack (ZIP)</span>
                </div>
                <ArrowRight className="text-gray-400" size={20} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Alternatives Band */}
      <AlternativesBand
        alternatives={[
          {
            title: "Need lowest upfront cost?",
            description: "GNG offers budget-friendly in-line filtration with a ring-lock quick-release head for easy maintenance.",
            link: "/filtration/inline/gng"
          },
          {
            title: "Prefer classic gasketed body in painted steel?",
            description: "BIG Series provides precision, gasket-sealed in-line filters with optional automation upgrades.",
            link: "/filtration/inline/big"
          }
        ]}
      />

      {/* Bottom CTA */}
      <section className="py-16 bg-gradient-to-br from-ctc-blue to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Optimize Your Filtration?</h2>
          <p className="text-xl text-blue-100 mb-8">
            Talk to a CTC specialist today to configure your BIG-S system for your specific flow rates, water quality, and operational requirements.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setIsContactModalOpen(true)}
              className="bg-white text-ctc-blue px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg inline-flex items-center justify-center"
            >
              <Mail className="mr-2" size={20} />
              Get a Quote
            </button>

            <Link
              to="/contact?topic=filtration"
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-ctc-blue transition-colors inline-flex items-center justify-center"
            >
              <Phone className="mr-2" size={20} />
              Talk to a Specialist
            </Link>
          </div>

          <div className="mt-8 pt-8 border-t border-white/20">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-blue-100">
              <div className="flex items-center">
                <Phone className="mr-2" size={20} />
                <span>(509) 901-7476</span>
              </div>
              <div className="flex items-center">
                <Mail className="mr-2" size={20} />
                <a href="mailto:Andre@ctcwaterworks.com" className="hover:text-white underline">
                  Andre@ctcwaterworks.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <UnifiedContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        productType="filtration"
        formSource="product_quote"
      />
    </div>
  );
};

export default BIGSSeriesPage;
